import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[#d7b369] text-[#791614] shadow-sm border border-[#c9a85e]/90 hover:bg-[#f9f6f0] hover:text-[#791614] hover:border-[#d7b369]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border-2 border-[#d7b369]/80 bg-background text-foreground hover:bg-[#d7b369]/14 hover:text-[#791614]",
        secondary:
          "border border-border bg-muted/50 text-foreground hover:bg-[#d7b369]/16 hover:text-[#791614]",
        ghost: "text-foreground hover:bg-[#d7b369]/16 hover:text-[#791614]",
        link: "text-[#791614] underline-offset-4 hover:underline hover:text-[#5a0f0e]",
        /** Ação primária em superfície clara: sempre texto claro sobre bordeaux (evita herdar texto escuro do variant default). */
        bordeaux: "bg-[#791614] text-white shadow-sm hover:bg-[#5a0f0e] hover:text-white focus-visible:ring-[#d7b369]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = "button", ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} type={type} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
